import React from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../components/content-box/ContentBox";
import PageActions from "../../../../base/PageActions";
import Breadcrumbs from "../../../../../components/breadcrumbs/Breadcrumbs";
import '../../../../styles/Common.scss';
import '../WorldPages.scss';
import '../WorldDynamicContent.scss';

interface HistoryPageProps {
}

const HistoryPage = (props: HistoryPageProps) => {

    const page: PageActions = new PageActions({
        title: `Casarok History - Chase the Fox`,
        description: "The world of Casarok has a history that follows a similar timeline to that of Earth, though interrupted by the Great Cataclysm!",
        keywords: ["casarok", "casarok history", "fantasy timeline", "fantasy world history", "great cataclysm"],
        imgSrc: `chase/world/geography/post-cataclysm.png`,
        url: `world/history`
    });

    return page.generateMetaContent(
        <>
            <ContentBox header="The History of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <p>
                        Casarok has a long and storied history. Modern historians have categorized the history of the
                        world into three eras made up of various ages.
                    </p>
                    <p>
                        The <strong>Creation Era</strong> represents the earliest existence of the planet and its
                        inhabitants prior to around 10,000 BCE when the first intelligent life was known to exist.
                        During this era the universe, planetary system, and Casarok itself was created by the will of
                        the <NavLink className="world-link" to="/world/religion/pantheon">gods and goddesses</NavLink>.
                        They also placed the primordials throughout the world in this era. Fossils from this time are
                        extremely sought after as they provide the only palpable knowledge of this time period.
                    </p>
                    <ul id="visible-element" className="spaced switch-visible">
                        <li>
                            <NavLink className="world-link" to='creation-age'>Creation Age</NavLink>: <em>(before 10,000
                            BCE)</em> The time before intelligent mortal life on Casarok.
                        </li>
                    </ul>
                    <p>
                        The <strong>Pre-Cataclysmic Era</strong> <em>(about 10,000 BCE - 5 ACE)</em> is the earliest
                        known time of intelligent life on the planet. All of the native
                        modern <NavLink className="world-link" to="/world/bestiary">races of Casarok</NavLink> appeared
                        on the planet during time in one manner or another. The pantheon of Casarok was particularly
                        active during this time. The majority of the history of this era is lost due to the War of the
                        Gods and the resulting Great Cataclysm.
                    </p>
                    <ul id="visible-element" className="spaced switch-visible">
                        <li>
                            <NavLink className="world-link" to='draconic-age'>Draconic Age</NavLink>: <em>(about 10,000
                            - 5,000 BCE)</em> The first intelligent life of
                            the <NavLink className="world-link" to="/world/bestiary/draconic">Draconic</NavLink> race
                            appears on Casarok.
                        </li>
                        <li>
                            <NavLink className="world-link" to='iron-age'>Iron Age</NavLink>: <em>(5,000 - 3,000
                            BCE)</em> The use of bronze and iron tools begins, and
                            the <NavLink className="world-link" to="/world/bestiary/aspect">Aspect</NavLink> and
                            the <NavLink className="world-link" to="/world/bestiary/anthro">Anthro</NavLink> races
                            emerge, though the latter is widely enslaved.
                        </li>
                        <li>
                            <NavLink className="world-link" to='rebellion-age'>Rebellion Age</NavLink>: <em>(3,000 -
                            1,500 BCE)</em> The Anthro race rapidly grows in number and magical strength, earning their
                            freedom.
                        </li>
                        <li>
                            <NavLink className="world-link" to='magical-age'>Magical Age</NavLink>: <em>(1,500 -
                            800 BCE)</em> Great advancements in the use of magic are made.
                        </li>
                        <li>
                            <NavLink className="world-link" to='empirical-age'>Empirical Age</NavLink>: <em>(800 -
                            550 BCE)</em> The world was divided into many large and powerful empires.
                        </li>
                        <li>
                            <NavLink className="world-link" to='crusade-age'>Crusade Age</NavLink>: <em>(550 - 400
                            BCE)</em> The Common language was invented and spread, improving relations between newly
                            forming nations.
                        </li>
                        <li>
                            <NavLink className="world-link" to='feudal-age'>Feudal Age</NavLink>: <em>(425 - 85
                            BCE)</em> Casarok is controlled by kingdoms that practice a mix of diplomacy and war.
                        </li>
                        <li>
                            <NavLink className="world-link" to='cataclysmic-age'>Cataclysmic Age</NavLink>: <em>(85
                            BCE - 5 ACE)</em> The War of the Gods occurs. The Great Cataclysm shatters the
                            world into continents.
                        </li>
                    </ul>
                    <p>
                        The <strong>Post-Cataclysmic Era</strong> <em>(5 ACE and onward)</em> is the most recent era
                        in which the world was divided into continents. Aside from one incident early in this age, the
                        world was devoid of the influence of the gods and goddesses of Casarok. They were all slain,
                        fell out of favor, or no longer took an active role in the events of the world. All advancement
                        was left to the intelligent races of the world. A strict record of history has been kept as
                        early as possible in this era so no more history would be lost to time.
                    </p>
                    <ul className="spaced">
                        <li>
                            <NavLink className="world-link" to='recovery-age'>Recovery Age</NavLink>: <em>(5 - 500
                            ACE)</em> The survivors of the Great Cataclysm began to rebuild.
                        </li>
                        <li>
                            <NavLink className="world-link" to='reemergence-age'>Reemergence Age</NavLink>: <em>(500 -
                            1000 ACE)</em> A new Feudal Age with a shift from magic to technology.
                        </li>
                        <li>
                            <NavLink className="world-link" to='renaissance-age'>Renaissance Age</NavLink>: <em>(1000 -
                            1300 ACE)</em> Technology flourishes and religion and government begin to separate.
                        </li>
                        <li>
                            <NavLink className="world-link" to='expansion-age'>Expansion Age</NavLink>: <em>(1300 -
                            1500 ACE)</em> Nations form into their more modern boundaries. Exploration and
                            recolonization occur.
                        </li>
                        <li>
                            <NavLink className="world-link" to='enlightened-age'>Enlightened Age</NavLink>: <em>(1500 -
                            1700 ACE)</em> Steam and clockwork powered machines become more widespread. Steampunk
                            culture enraptures the people of Casarok.
                        </li>
                        <li>
                            <NavLink className="world-link" to='industrial-age'>Industrial Age</NavLink>: <em>(1700 -
                            1800 ACE)</em> Fossil fuels, electricity, and factories overtake steam and clockwork power.
                            A wealth and quality of life boom occurs.
                        </li>
                        <li>
                            <NavLink className="world-link" to='machination-age'>Machination Age</NavLink>: <em>(1800
                            BCE - 1900 ACE)</em> Mage-tech is invented and machines become more powerful, but
                            the Machinization War grips the world.
                        </li>
                        <li>
                            <NavLink className="world-link" to='modern-age'>Modern Age</NavLink>: <em>(1900
                            - 2100 ACE)</em> The world is at tentative
                            peace. <NavLink className="world-link" to="/characters/chase">Chase</NavLink> and his
                            friends live during this time.
                        </li>
                        <li>
                            <strong>Space Age</strong>: <em>(2100 - 2300 ACE)</em> Planetary system colonization and
                            exploration begins. Alien races are discovered by Casarokans.
                        </li>
                        <li>
                            <strong>Lightspeed Age</strong>: <em>(2300 ACE and beyond)</em> Light-speed travel becomes
                            possible. Casarokans must frontier to the depths of space to push their limits further.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default HistoryPage;